import { apiFetch } from "./api";
import { createDevConsole } from "../utils/devLogger";

const console = createDevConsole();

class RelationshipCacheService {
  constructor() {
    this.cache = new Map();
    this.cacheTimestamp = null;
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes
    this.isLoading = false;
    this.loadPromise = null;
  }

  async getRelationships() {
    // Check if cache is still valid
    if (this.cache.has("relationships") && this.isCacheValid()) {
      console.log("🔍 RelationshipCache: Using cached relationships");
      return this.cache.get("relationships");
    }

    // If already loading, return the existing promise
    if (this.isLoading && this.loadPromise) {
      console.log("🔍 RelationshipCache: Waiting for existing load promise");
      return this.loadPromise;
    }

    // Start loading
    this.isLoading = true;
    this.loadPromise = this.loadRelationshipsFromAPI();

    try {
      const relationships = await this.loadPromise;
      return relationships;
    } finally {
      this.isLoading = false;
      this.loadPromise = null;
    }
  }

  async loadRelationshipsFromAPI() {
    try {
      console.log("🔍 RelationshipCache: Loading relationships from API");
      const response = await apiFetch(
        "/api/relationships-from-database?limit=10000",
        { timeout: 30000 }
      );

      if (response.ok) {
        const data = await response.json();
        const relationships = data.relationships || data.data || [];

        console.log(
          `🔍 RelationshipCache: Loaded ${relationships.length} relationships from API`
        );

        // Cache the relationships
        this.cache.set("relationships", relationships);
        this.cacheTimestamp = Date.now();

        return relationships;
      } else {
        console.warn("RelationshipCache: API returned error:", response.status);
        return [];
      }
    } catch (error) {
      console.error("RelationshipCache: Error loading relationships:", error);
      return [];
    }
  }

  isCacheValid() {
    if (!this.cacheTimestamp) return false;
    return Date.now() - this.cacheTimestamp < this.cacheTimeout;
  }

  // Get relationships for a specific entity (with pagination to fetch all)
  // Optimized to use cache first, then API if needed
  async getRelationshipsForEntity(entityId) {
    // Strategy 1: Try cache first (fast path)
    if (this.cache.has("relationships") && this.isCacheValid()) {
      const cachedRelationships = this.cache.get("relationships");
      const entityRelationships = cachedRelationships.filter(
        (rel) =>
          rel.source_entity_id === entityId || rel.target_entity_id === entityId
      );

      // If we found relationships in cache, use them (fast path)
      if (entityRelationships.length > 0) {
        console.log(
          `🔍 RelationshipCache: Using ${entityRelationships.length} relationships from cache for entity ${entityId}`
        );
        return entityRelationships;
      }

      // If cache is valid but no relationships found, the entity might have no relationships
      // Still check API to be sure (in case cache is incomplete)
      console.log(
        `🔍 RelationshipCache: No relationships in cache for entity ${entityId}, checking API...`
      );
    }

    // Strategy 2: Fetch from API (ensures we have all relationships, handles pagination)
    try {
      // First, get the first page to check total count
      const firstPageUrl = `/api/relationships/by-entity/${entityId}?limit=1000&offset=0`;
      const firstResponse = await apiFetch(firstPageUrl, { timeout: 20000 });
      if (firstResponse.ok) {
        const firstData = await firstResponse.json();
        const firstPage = firstData.data || [];
        const totalItems =
          firstData.pagination?.total_items || firstPage.length;

        console.log(
          `🔍 RelationshipCache: Entity ${entityId} has ${totalItems} total relationships, fetched ${firstPage.length} in first page`
        );

        // If we got all relationships in the first page, return them
        if (firstPage.length >= totalItems || firstPage.length < 1000) {
          return firstPage;
        }

        // Otherwise, fetch remaining pages
        const allRelationships = [...firstPage];
        const pageSize = 1000;
        let offset = pageSize;

        while (allRelationships.length < totalItems && offset < totalItems) {
          const pageUrl = `/api/relationships/by-entity/${entityId}?limit=${pageSize}&offset=${offset}`;
          try {
            const pageResponse = await apiFetch(pageUrl, { timeout: 20000 });
            if (pageResponse.ok) {
              const pageData = await pageResponse.json();
              const pageRelationships = pageData.data || [];
              if (pageRelationships.length === 0) break; // No more relationships
              allRelationships.push(...pageRelationships);
              offset += pageSize;
              console.log(
                `🔍 RelationshipCache: Fetched page at offset ${offset - pageSize}, total so far: ${allRelationships.length}`
              );
            } else {
              break; // Stop if we get an error
            }
          } catch (e) {
            console.warn(
              `RelationshipCache: Error fetching page at offset ${offset}:`,
              e
            );
            break; // Stop on error
          }
        }

        console.log(
          `🔍 RelationshipCache: Fetched ${allRelationships.length} total relationships for entity ${entityId} from API`
        );
        return allRelationships;
      }
    } catch (e) {
      console.warn(
        "RelationshipCache: by-entity endpoint failed, falling back to local cache",
        e
      );
    }

    // Strategy 3: Fallback to cached relationships (even if cache might be stale)
    const relationships = await this.getRelationships();
    const filtered = relationships.filter(
      (rel) =>
        rel.source_entity_id === entityId || rel.target_entity_id === entityId
    );
    console.log(
      `🔍 RelationshipCache: Using ${filtered.length} relationships from fallback cache for entity ${entityId}`
    );
    return filtered;
  }

  // Get connected entity IDs for a specific entity
  async getConnectedEntityIds(entityId) {
    const relationships = await this.getRelationshipsForEntity(entityId);
    const connectedIds = new Set();

    relationships.forEach((rel) => {
      if (rel.source_entity_id === entityId) {
        connectedIds.add(rel.target_entity_id);
      } else {
        connectedIds.add(rel.source_entity_id);
      }
    });

    return Array.from(connectedIds);
  }

  // Clear cache (useful for refresh)
  clearCache() {
    this.cache.clear();
    this.cacheTimestamp = null;
    console.log("🔍 RelationshipCache: Cache cleared");
  }

  // Get cache stats
  getCacheStats() {
    return {
      hasRelationships: this.cache.has("relationships"),
      relationshipCount: this.cache.get("relationships")?.length || 0,
      cacheAge: this.cacheTimestamp ? Date.now() - this.cacheTimestamp : null,
      isValid: this.isCacheValid(),
      isLoading: this.isLoading,
    };
  }
}

// Export singleton instance
export const relationshipCache = new RelationshipCacheService();
